import { Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '@equine-ui-nx/account';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatExpansionModule } from '@angular/material/expansion'; // Add this import
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { ToolbarCartButtonComponent } from '@equine-ui-nx/payments';
import { ConfirmationDialogComponent, Roles } from '@equine-ui-nx/ui';
import { RoleLabelPipe } from '../pipe/role-label.pipe';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FooterComponent } from '../eqform';
import { SeasonDetailService } from 'libs/iea/protocols/season/src/lib/iea-season/season-detail/data-access/season-detail.service';
import { SeasonConfirmationDialogComponent } from '../dialogs/season-confirmation-dialog/season-confirmation-dialog.component';

interface MenuItem {
  name: string;
  icon: string;
  children?: MenuItem[];
  expanded?: boolean;
  roles: any[];
  link: string;
  type: string;
  childrenCountRequired?: boolean;
}

@Component({
  selector: 'equine-ui-nx-navbar',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    MatSidenavModule,
    MatListModule,
    MatMenuModule,
    MatExpansionModule,
    ToolbarCartButtonComponent,
    CdkAccordionModule,
    RoleLabelPipe,
    MatTooltipModule,
    FooterComponent
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit {
  @Input() isToolbarShow: boolean = false;
  @Input() showOneEquineLogoInFooter: boolean = true;
  @ViewChild('sideNav') sidNav!: any;
  user: any;
  routLinks: MenuItem[] = []
  loggedInUserName = '';
  role = '';
  profilePic = '';

  userId: string | null = null;
  hasChildren = false;
  windowMode: any = 'side';
  oboUser: any = null;
  seasonList: any = []
  selectedSeason: any;
  hasShownOboUserPopup: boolean = false;
  unsubscribe$ = new Subject<void>();

  constructor(
    public readonly authService: AuthService, 
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly seasonDetailService: SeasonDetailService
  ) { }

  ngOnInit(): void {
    this.authService
      .listenToLoggedinUser()
      .pipe(
        filter((user) => !!user),
        tap((user) => (this.userId = this.getUserId(user)))
      )
      .subscribe((user: any) => {
        // Only executed if user exists because of the filter
        this.user = user;
        this.loggedInUserName = `${this.user?.firstName} ${this.user?.lastName}`;
        this.loggedInUserName = this.loggedInUserName.toUpperCase();
        this.role = this.user?.roles[0]?.name;
        this.oboUser = this.authService.getDataFromSessionStg("obo")
        // this.role = this.role?.toUpperCase();
        this.openOBOConfirmationDialog()
        this.getProfilePic(user);
        
        if(user?.childrenCount && !isNaN(user?.childrenCount) && user?.childrenCount > 0) {
          this.hasChildren = true;
        } else {
          this.hasChildren = false;
        }
        this.setRoutLink(user._id);
      });
    this.setWindowMode(window.innerWidth);

    this.authService
      .listenToSeasonSelectionConfirmation()
      .subscribe(() => {
        if(this.user) {
          this.seasonList = [];
          this.authService.emitSelectedSeason(null);
          this.selectedSeason = null;
          this.getAllSeason({});
        }
      });
  }

  setRoutLink(userId: string | null) {
    this.routLinks = [
      {
        name: 'Dashboard',
        link: '/dashboard',
        type: 'link',
        icon: 'home',
        roles: [
          Roles.ACM,
          Roles.JEM,
          Roles.YOUTH_RIDER,
          Roles.USER,
          Roles.COACH
        ],
      },
      {
        name: 'Account Profile',
        link: '/profile',
        type: 'link',
        icon: 'person',
        roles: [
          Roles.ACM,
          Roles.JEM,
          Roles.YOUTH_RIDER,
          Roles.USER,
          Roles.COACH
        ],
      },
      {
        name: 'Manage My Youth',
        link: '/profile/manage-youth/rider',
        type: 'link',
        icon: 'account_circle',
        roles: [Roles.ACM, Roles.USER, Roles.COACH],
        childrenCountRequired: true,
      },
      {
        name: 'Create IEA Membership',
        link: '/membership',
        icon: 'star_border',
        type: 'link',
        roles: [Roles.USER],
        childrenCountRequired: false
      },
      {
        name: 'My Membership',
        link: `/profile/youth/${userId}/membership`,
        icon: 'star_border',
        type: 'link',
        roles: [Roles.JEM, Roles.YOUTH_RIDER],
      },
      {
        name: 'My Membership',
        link: `/profile/adult-user/${userId}/membership`,
        icon: 'star_border',
        type: 'link',
        roles: [Roles.COACH, Roles.ACM],
      },
      // for Users
      {
        name: 'Teams',
        link: '',
        icon: 'people',
        type: 'dropdown',
        children: [
          {
            name: 'View My Teams',
            link: `/profile/adult-user/${userId}/teams`,
            icon: '',
            type: 'link',
            roles: [Roles.USER],
          },
          {
            name: 'Join a Team',
            link: '/membership/join-team',
            icon: '',
            type: 'link',
            roles: [Roles.USER],
          }
        ],
        roles: [Roles.USER],
        childrenCountRequired: true
      },
      // not Users
      {
        name: 'Teams',
        link: '',
        icon: 'people',
        type: 'dropdown',
        children: [
          {
            name: 'View My Teams',
            link: `/profile/adult-user/${userId}/teams`,
            icon: '',
            type: 'link',
            roles: [Roles.COACH, Roles.ACM],
          },
          {
            name: 'Create/Renew a Team',
            link: '/membership/team',
            icon: '',
            type: 'link',
            roles: [Roles.COACH],
          },
          {
            name: 'Join a Team',
            link: '/membership/join-team',
            icon: '',
            type: 'link',
            roles: [Roles.ACM, Roles.COACH],
          },
        ],
        roles: [Roles.ACM, Roles.COACH],
      },
      /* {
        name: 'Teams',
        link: `/profile/adult-user/${userId}/teams`,
        icon: 'people',
        type: 'link',
        roles: [Roles.USER],
        childrenCountRequired: true
      }, */
      {
        name: 'Teams',
        link: `/profile/youth/${userId}/teams`,
        icon: 'people',
        type: 'link',
        roles: [Roles.JEM, Roles.YOUTH_RIDER],
      },
      //user
      // {
      //   name: 'Shows',
      //   link: '',
      //   icon: 'workspace_premium',
      //   type: 'dropdown',
      //   children: [
      //     {
      //       name: 'Show Calendar',
      //       link: '/shows/calendar',
      //       icon: '',
      //       type: 'link',
      //       roles: [Roles.USER],
      //     },
      //     {
      //       name: 'My Shows',
      //       link: `/profile/adult-user/${userId}/shows`,
      //       icon: '',
      //       type: 'link',
      //       roles: [Roles.USER],
      //     }
      //   ],
      //   roles: [Roles.USER],
      //   childrenCountRequired: true
      // },
      // others
      {
        name: 'Shows',
        link: '',
        icon: 'workspace_premium',
        type: 'dropdown',
        children: [
          {
            name: 'Show Calendar',
            link: '/shows/calendar',
            icon: '',
            type: 'link',
            roles: [Roles.YOUTH_RIDER, Roles.JEM, Roles.ACM, Roles.COACH, Roles.USER],
          },
          // {
          //   name: 'View my Shows',
          //   link: '/application-list/show',
          //   icon: '',
          //   type: 'link',
          //   roles: [Roles.ACM, Roles.COACH],
          // },
          {
            name: 'My Shows',
            link: `/profile/adult-user/${userId}/shows`,
            icon: '',
            type: 'link',
            roles: [Roles.ACM, Roles.COACH],
          },
          {
            name: 'My Shows',
            link: `/profile/youth/${userId}/shows`,
            icon: '',
            type: 'link',
            roles: [Roles.YOUTH_RIDER, Roles.JEM],
          }
        ],
        roles: [Roles.YOUTH_RIDER, Roles.JEM, Roles.ACM, Roles.COACH, Roles.USER],
      },
      // {
      //   name: 'Shows',
      //   link: '/application-list/show',
      //   icon: 'workspace_premium',
      //   type: 'link',
      //   roles: [Roles.USER],
      //   childrenCountRequired: true
      // },
      // {
      //   name: 'Shows',
      //   link: `profile/youth/${userId}/shows`,
      //   icon: 'workspace_premium',
      //   type: 'link',
      //   roles: [Roles.JEM, Roles.YOUTH_RIDER],
      // },
      {
        name: 'Applications',
        link: '',
        icon: 'description',
        type: 'link',
        children: [
          {
            name: 'Member Applications',
            link: '/application-list/membership',
            icon: '',
            type: 'link',
            roles: [Roles.ACM, Roles.USER, Roles.COACH]
          },
          {
            name: 'Team Applications',
            link: '/application-list/team',
            icon: '',
            type: 'link',
            roles: [Roles.ACM, Roles.COACH]
          },
          {
            name: 'Show Host Applications',
            link: '/application-list/show',
            icon: '',
            type: 'link',
            roles: [Roles.COACH, Roles.ACM]
          }
        ],
        roles: [Roles.ACM, Roles.USER, Roles.COACH]
      },
      // will add on v2
      {
        name: 'Performance',
        link: '/#',
        icon: 'workspace_premium',
        type: 'link',
        roles: [Roles.JEM, Roles.YOUTH_RIDER, Roles.USER, Roles.COACH, Roles.ACM, Roles.ADULT_RIDER],
        children: [
          {
            name: "My Performance",
            link: `profile/adult-user/${userId}/performance`,
            icon: "",
            type: "link",
            roles: [Roles.COACH, Roles.JEM, Roles.YOUTH_RIDER, Roles.ACM, Roles.ADULT_RIDER]
          },
          {
            name: "Points & Results",
            link: "/performance/pr-search",
            icon: "",
            type: "link",
            roles: [Roles.COACH, Roles.JEM, Roles.YOUTH_RIDER, Roles.ACM, Roles.ADULT_RIDER, Roles.USER]
          },
        ]
      }
    ]
  }

  login() {
    this.router.navigate(['/account/login']);
  }

  getAllSeason(query: any) {
    query.aggregate =  true;
    query.limit = 50;
    query.excludeIncomplete = true;
    query.sort = "byName";
    query.userActiveSeason = true;
    this.seasonDetailService.listAllSeason(query)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe({
      next: (res: any) => {
        this.seasonList = res.data;
        if(this.seasonList.length > 1 && !this.router.url.includes("account/login")) {
          this.openSeasonSelectionDialog();
        } else {
          const activeSeason = this.seasonList.find((season: any) => season.status === "active" && season.isActive);
          this.onChangeSeason(activeSeason ? activeSeason: this.seasonList[0]);
        }
      },
    });
  }

  openSeasonSelectionDialog() {
    const seasonDialogRef = this.dialog.open(SeasonConfirmationDialogComponent, {
      panelClass: ["col-12", "col-md-4", "col-lg-4"],
      autoFocus: false,
      disableClose: true,
      data: { 
        seasonList: this.seasonList
      }
    });

    seasonDialogRef.componentInstance.selectedSeasonEmit.subscribe(({ seasonId }) => {
      const season = this.seasonList.find((season: any) => season._id === seasonId);
      this.onChangeSeason(season, true);
      seasonDialogRef.close();
    });
  }

  onChangeSeason(activeSeason: any, forceUpdate: boolean = false) {
    const selectedSeason = localStorage.getItem('selectedSeason');
    if (!forceUpdate && selectedSeason && Object.keys(JSON.parse(selectedSeason)).length) {
      const seasonData = this.seasonList.find((season: any) => season._id === JSON.parse(selectedSeason)?._id);
      if (seasonData) activeSeason = seasonData;
    }
    this.selectedSeason = activeSeason;
    this.getUserDataForSelectedSeason(this.selectedSeason._id);
    this.authService.emitSelectedSeason(this.selectedSeason);
    localStorage.setItem('selectedSeason', JSON.stringify(activeSeason));
  }

  getUserDataForSelectedSeason(seasonId: string): void {
    const userId = this.authService.getUserId();
    if(!userId) return;
    this.authService.getUserDataForSelectedSeason(seasonId, userId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (user) => {
          this.authService.emitLoggedinUser(user);
          this.authService.saveUserDataInLocalStorage(JSON.stringify(user));
          const userRoles: string[] = [];
          for (let i = 0; i < user?.roles?.length; i++) {
            if (user?.roles[i]?.name) {
              userRoles.push(user?.roles[i]?.name);
            }
          }
          if (userRoles.length) {
            this.authService.getUserPermissionByRole(userRoles)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe({
                next: (data: any) => {
                  if (data?.status) {
                    this.authService.setUserPermissions(JSON.stringify(data?.permissions));
                  }
                }
              })
          }
        }
      })
  }

  getProfilePic(user: any): void {
    this.authService.getProfilePicUrl()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        this.profilePic = data;
        if(!this.profilePic && user?.profilePic) {
          this.authService.getSecuredUrl(user.profilePic).subscribe({
            next: (data)=>{
              this.profilePic = data.url;
              this.authService.setProfilePicUrl(data.url);
            }
          })
        }
      });
  }

  async logout(id: any) {
    let refreshToken = await this.authService.getAccessToken();
    let userData = await this.authService.getUserData();
    let accessToken = await this.authService.getAccessToken();
    sessionStorage.clear();
    let data = {
      accessToken: accessToken,
      selectedValue: id,
    };
    this.authService.tokenUpdate(data).subscribe((res) => {
      return res;
    });
    if (accessToken) {
      this.authService.removeAccessToken();
    }
    if (refreshToken) {
      this.authService.removeRefreshToken();
    }
    if (userData) {
      this.authService.removeUserData();
      this.authService.removeUserPermission();
    }
    if (this.authService.getURL()) {
      this.authService.removeCacheUrl();
    }
    this.authService.setProfilePicUrl('');
    //make isloginstatus as false at logout
    this.authService.setIsUserLogin(false);
    this.router.navigate(['/account/login']);
    this.authService.logout();
    this.authService.clearSeasonData();
    this.router.navigate(['/account/login']);

    this.sidNav.toggle();
    this.sidNav.close();
  }

  openOBOConfirmationDialog(){
    if(this.oboUser && !this.hasShownOboUserPopup){
      this.hasShownOboUserPopup = true;
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '700px',
        data: {
          header: `You are now doing work on behalf of ${this.loggedInUserName}. Select End OBO Session above to logout as this User and return to the IEA Workspace.`,
          acceptLabel: "CONTINUE",
          rejectLabel: "GO BACK"
        }
      });
      dialogRef.afterClosed().subscribe((result)=>{
        if(result == null){
          this.closeOBO();
        }
        
      })
    }
  }
  closeOBO(){
    this.authService.closeOBOWindow()
  }

  getUserId(user: any): string {
    return user._id;
  }

  toggleItem(item: MenuItem) {
    item.expanded = !item.expanded;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const target = event.target as Window;
    this.windowMode = (target.innerWidth < 768) ? 'over' : 'side';
  }

  private setWindowMode(innerWidth: number): void {
    this.windowMode = (innerWidth < 768) ? 'over' : 'side';
  }

  goToDonation() {
    this.router.navigate(['donation'])
  }

  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
